/* @license
 * Copyright 2026 k1pp0
 * SPDX-License-Identifier: MIT
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, LitElement } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { WebXRCaptureProvider } from './webxr-capture-provider.js';
import CaptureCloseIcon from './assets/capture-close-svg.js';
import CaptureDownloadIcon from './assets/capture-download-svg.js';
import { ensureFrameHook, ensureGlobalPatches, getARRenderer, getThreeRenderer, getXRSession, subscribe } from './host-bridge.js';
import { captureUIStyles } from './styles/capture-ui.css.js';
const PREVIEW_FADE_MS = 500;
/**
 * Companion element that adds WebXR AR screenshot capture, plus a built-in
 * shutter / flash / preview UI, on top of `<model-viewer>`. All capture
 * implementation lives in this package — installing it is what enables the
 * feature; removing it leaves `<model-viewer>` capture-free.
 *
 * Usage:
 *
 *   <model-viewer id="mv" src="…" ar>
 *     <model-viewer-webxr-capture></model-viewer-webxr-capture>
 *   </model-viewer>
 *
 * The `for` attribute resolves to a sibling `<model-viewer>` by id. If
 * omitted, the closest ancestor `<model-viewer>` is used.
 */
let ModelViewerWebXRCapture = class ModelViewerWebXRCapture extends LitElement {
    constructor() {
        super(...arguments);
        this.actionText = 'Save / Share';
        this.filenamePrefix = 'webxr-snap';
        this.shareTitle = 'WebXR Snap';
        this.host = null;
        this.provider = new WebXRCaptureProvider();
        this.unsubscribeFrame = null;
        this.sessionActive = false;
        this.currentBlob = null;
        this.currentUrl = null;
        this.clearSrcTimer = 0;
        this.onFrameTick = (view, scene, viewCamera) => {
            this.provider.onFrame(view, scene, viewCamera);
        };
        this.onARStatus = (event) => {
            const status = event.detail.status;
            if (status === 'session-started') {
                if (this.host != null) {
                    try {
                        const threeRenderer = getThreeRenderer(this.host);
                        const session = getXRSession(this.host);
                        if (session != null) {
                            this.provider.onSessionStart(threeRenderer, session);
                            this.sessionActive = true;
                        }
                    }
                    catch (err) {
                        console.warn('[model-viewer-webxr-capture] could not initialize capture for ' +
                            'session-started:', err);
                    }
                }
                this.updateShutterVisibility();
            }
            else if (status === 'not-presenting' || status === 'failed') {
                if (this.sessionActive) {
                    this.provider.onSessionEnd();
                    this.sessionActive = false;
                }
                this.classList.remove('shutter-enabled');
                this.hidePreview();
            }
        };
        this.onBeforeXRSelect = (event) => {
            event.preventDefault();
        };
        this.onShutterClick = () => {
            const flash = this.flashEl;
            if (flash != null) {
                // Match 8thwall: instant white (.flashing has 0s transition), then
                // fade back via the default 0.6s opacity transition.
                flash.classList.add('flashing');
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => flash.classList.remove('flashing'));
                });
            }
            this.captureWebXRScreenshot()
                .then((blob) => {
                if (blob != null) {
                    this.showPreview(blob);
                }
            })
                .catch((err) => {
                console.warn('[model-viewer-webxr-capture] capture failed:', err);
            });
        };
        this.hidePreview = () => {
            var _a, _b;
            if (!this.classList.contains('preview-open')) {
                return;
            }
            (_a = this.previewEl) === null || _a === void 0 ? void 0 : _a.classList.remove('fade-in');
            // Bring the shutter back via the same fade transition.
            (_b = this.shutterEl) === null || _b === void 0 ? void 0 : _b.classList.remove('fade-container');
            // Hide the <img> immediately so the broken-image icon never flashes
            // through the fade-out; clear src after the transition completes.
            if (this.previewImg != null) {
                this.previewImg.style.visibility = 'hidden';
            }
            if (this.clearSrcTimer !== 0) {
                clearTimeout(this.clearSrcTimer);
            }
            this.clearSrcTimer = window.setTimeout(() => {
                if (this.previewImg != null) {
                    this.previewImg.removeAttribute('src');
                }
                this.classList.remove('preview-open');
                this.releaseCurrentBlob();
                this.clearSrcTimer = 0;
            }, PREVIEW_FADE_MS);
            this.dispatchEvent(new CustomEvent('webxr-capture-preview-closed'));
        };
        this.onActionClick = async () => {
            var _a;
            const blob = this.currentBlob;
            const url = this.currentUrl;
            if (blob == null || url == null) {
                return;
            }
            const filename = `${this.filenamePrefix}-${Date.now()}.jpg`;
            const file = new File([blob], filename, { type: blob.type });
            const nav = navigator;
            if ((_a = nav.canShare) === null || _a === void 0 ? void 0 : _a.call(nav, { files: [file] })) {
                try {
                    await nav.share({ files: [file], title: this.shareTitle });
                    return;
                }
                catch (err) {
                    if (err != null && err.name === 'AbortError') {
                        return;
                    }
                    console.warn('[model-viewer-webxr-capture] share failed, falling back to download:', err);
                }
            }
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
        };
    }
    /**
     * True when the active WebXR AR session has the `camera-access` feature
     * enabled. False before the session starts, after it ends, or when no
     * session is active.
     */
    get canCaptureWebXRScreenshot() {
        return this.provider.supported;
    }
    /**
     * Captures the current WebXR AR view (camera passthrough composed with the
     * rendered 3D scene) as a Blob. Resolves with `null` when capture is
     * unavailable. Also dispatches an `ar-screenshot` event with the result.
     */
    async captureWebXRScreenshot(options) {
        const blob = await this.provider.requestCapture(options);
        this.dispatchEvent(new CustomEvent('ar-screenshot', { detail: { blob, success: blob !== null } }));
        return blob;
    }
    connectedCallback() {
        super.connectedCallback();
        // Suppress WebXR transient-input select for taps that hit our overlay UI,
        // per the WebXR DOM Overlays spec — otherwise a shutter / preview tap also
        // re-places or drags the AR model.
        this.addEventListener('beforexrselect', this.onBeforeXRSelect);
        this.resolveHost();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('beforexrselect', this.onBeforeXRSelect);
        this.detachHost();
        this.releaseCurrentBlob();
    }
    updated(changed) {
        if (changed.has('for')) {
            this.detachHost();
            this.resolveHost();
        }
    }
    render() {
        return html `
      <div class="flash" aria-hidden="true"></div>
      <div class="shutter" part="shutter">
        <svg class="progress-container" viewBox="0 0 38 38" aria-hidden="true">
          <circle class="progress-track" cx="19" cy="19" r="16"></circle>
        </svg>
        <button class="recorder-button style-reset" type="button"
            aria-label="Capture AR snapshot"
            @click=${this.onShutterClick}></button>
      </div>
      <div class="preview" part="preview" role="dialog"
          aria-label="AR snapshot preview">
        <div class="top-bar">
          <div class="preview-box">
            <button class="preview-close style-reset icon-button" type="button"
                aria-label="Close preview" @click=${this.hidePreview}>
              ${CaptureCloseIcon}
            </button>
            <img class="preview-image" alt="AR snapshot preview">
          </div>
        </div>
        <div class="bottom-bar">
          <button class="action-button style-reset" type="button"
              @click=${this.onActionClick}>
            <span class="action-text">${this.actionText}</span>
            ${CaptureDownloadIcon}
          </button>
        </div>
      </div>
    `;
    }
    resolveHost() {
        var _a;
        const root = this.getRootNode();
        const host = (this.for != null && this.for.length > 0) ?
            (_a = root.getElementById) === null || _a === void 0 ? void 0 : _a.call(root, this.for) :
            this.closest('model-viewer');
        if (host == null) {
            return;
        }
        this.host = host;
        try {
            ensureGlobalPatches();
            ensureFrameHook(host);
            this.unsubscribeFrame = subscribe(host, this.onFrameTick);
        }
        catch (err) {
            console.warn('[model-viewer-webxr-capture] failed to attach to <model-viewer>:', err);
            this.host = null;
            return;
        }
        host.addEventListener('ar-status', this.onARStatus);
        // Plugin attached after AR is already running cannot retroactively
        // request `'camera-access'` for the live session — capture will be
        // available only on the next AR session.
        try {
            if (getARRenderer(host).isPresenting) {
                console.warn('[model-viewer-webxr-capture] attached while AR session is active; ' +
                    'capture will be available on the next AR session.');
            }
        }
        catch (_b) {
            // Symbol resolution already validated above; this is a safety net.
        }
    }
    detachHost() {
        if (this.host == null) {
            return;
        }
        this.host.removeEventListener('ar-status', this.onARStatus);
        if (this.unsubscribeFrame != null) {
            this.unsubscribeFrame();
            this.unsubscribeFrame = null;
        }
        if (this.sessionActive) {
            this.provider.onSessionEnd();
            this.sessionActive = false;
        }
        this.host = null;
    }
    updateShutterVisibility() {
        if (this.canCaptureWebXRScreenshot) {
            this.classList.add('shutter-enabled');
        }
        else {
            this.classList.remove('shutter-enabled');
        }
    }
    showPreview(blob) {
        var _a, _b;
        this.releaseCurrentBlob();
        this.currentBlob = blob;
        this.currentUrl = URL.createObjectURL(blob);
        if (this.previewImg != null) {
            this.previewImg.style.visibility = '';
            this.previewImg.src = this.currentUrl;
        }
        this.classList.add('preview-open');
        (_a = this.previewEl) === null || _a === void 0 ? void 0 : _a.classList.add('fade-in');
        // Hide the shutter behind the preview during the fade-in.
        (_b = this.shutterEl) === null || _b === void 0 ? void 0 : _b.classList.add('fade-container');
        this.dispatchEvent(new CustomEvent('webxr-capture-preview-opened', { detail: { blob } }));
    }
    releaseCurrentBlob() {
        if (this.currentUrl != null) {
            URL.revokeObjectURL(this.currentUrl);
        }
        this.currentUrl = null;
        this.currentBlob = null;
    }
};
ModelViewerWebXRCapture.styles = captureUIStyles;
__decorate([
    property({ type: String })
], ModelViewerWebXRCapture.prototype, "for", void 0);
__decorate([
    property({ type: String, attribute: 'action-text' })
], ModelViewerWebXRCapture.prototype, "actionText", void 0);
__decorate([
    property({ type: String, attribute: 'filename-prefix' })
], ModelViewerWebXRCapture.prototype, "filenamePrefix", void 0);
__decorate([
    property({ type: String, attribute: 'share-title' })
], ModelViewerWebXRCapture.prototype, "shareTitle", void 0);
__decorate([
    query('.flash')
], ModelViewerWebXRCapture.prototype, "flashEl", void 0);
__decorate([
    query('.shutter')
], ModelViewerWebXRCapture.prototype, "shutterEl", void 0);
__decorate([
    query('.preview')
], ModelViewerWebXRCapture.prototype, "previewEl", void 0);
__decorate([
    query('.preview-image')
], ModelViewerWebXRCapture.prototype, "previewImg", void 0);
ModelViewerWebXRCapture = __decorate([
    customElement('model-viewer-webxr-capture')
], ModelViewerWebXRCapture);
export { ModelViewerWebXRCapture };
//# sourceMappingURL=model-viewer-webxr-capture.js.map